import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import BudgetModes from '@/common/enums/budget-modes.enum';
import ReachAndFrequencyStatus from '@/common/enums/reach-and-frequency-status.enum';
import { IABCategory, LineItem, QueryParams } from '@/common/models';
import Advertiser, { IAdvertiserAPI } from '@/common/models/advertisers/Advertiser';
import CachedPacing, { ICachedPacingAPI } from '@/common/models/CachedPacing';
import { getEntityProperties } from '@/common/models/campaign/Campaign.entityProperties';
import Exception from '@/common/models/Exception';
import Flight, { ICampaignFlightAPI } from '@/common/models/flight/Flight';
import MarginValue, { IMarginValueAPI } from '@/common/models/margins/MarginValue';
import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import Account, { IAccountAPI } from '@/common/models/misc-api-models/Account';
import { FilterCollection, FilterComparisonOperator } from '@/common/models/query-params';
import SharedPublicEntity, { TSharedPublicEntityAPI } from '@/common/models/SharedPublicEntity';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH, fromServerDateTime, FullDaypart, getStore, toServerDateTimeNoTimezone } from '@/globals';
import { getDiff } from '@/utils/Diff';
import formatNumber from '@/utils/NumberFormatter';
import QuickMathUtils from '@/utils/QuickMathUtils';
import { mapOrgAppGetters } from '@/utils/StateMapping';
import Validatable, { IEntityChange, IGetEntityChanges } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import CampaignProperty, { ICampaignPropertyAPI } from './CampaignProperty';
import { IOrganizationSharedCampaign } from './SharedCampaign';

export interface ICampaignAPI {
  campaign_id: number;
  name: string;
  state?: string;
  notes: string;
  advertiser_id: number;
  advertiser?: IAdvertiserAPI;
  start_date?: string;
  end_date?: string;
  budget: number;
  targeted_impressions: number;
  use_manual_impressions: boolean;
  iab_categories?: IABCategory[];
  account_id: number;
  account?: IAccountAPI;
  organization_id?: number;
  min_seconds_between_plays: number;
  created_on_utc?: string;
  modified_on_utc?: string;
  cachedpacing?: ICachedPacingAPI;
  campaign_properties: ICampaignPropertyAPI[];
  budget_cap: number;
  cap: number;
  pacing_budget: number;
  pacing_budget_mode: BudgetModes;
  pacing_modified_at: string;
  pacing_strategy: string;
  priority: number;
  margins: IMarginValueAPI[];
  daypart?: string;
  last_played_on_utc?: string;
  created_by?: number;
  campaign_internal_properties: ICampaignPropertyAPI[];
  plan_id: number;
  reach_and_frequency_status: ReachAndFrequencyStatus;
  shared_entity?: TSharedPublicEntityAPI;
  shared_campaign?: TSharedPublicEntityAPI;
  uuid?: string;
  flights: ICampaignFlightAPI[];
  campaign_type: CampaignType;
  insertion_order_budget: number;
  test_campaign: boolean;
  advertiser_budget: number;
}

export enum CampaignExpandOptions {
  Flights = 'flights',
}

export enum CampaignType {
  Normal = 'NORMAL',
  Smart = 'SMART',
  Flight = 'SMART_FLIGHT',
}

export default class Campaign extends Validatable<Campaign> implements IGetEntityChanges<Campaign> {
  static type: string = 'Campaign';

  apiObject: ICampaignAPI;
  cachedOriginalObject: Campaign;

  name: string;
  startDate?: DateTime;
  endDate?: DateTime;
  advertiserID: number;
  advertiser?: Advertiser;
  accountID: number;
  account?: Account;
  budget: number;
  targetedImpressions: number;
  useManualImpressions: boolean;
  lineItems: LineItem[];
  iabCategories: IABCategory[];
  notes: string;
  timestamp: DateTime;
  organizationID?: number;
  minSecondsBetweenPlays: number;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  cachedPacing?: CachedPacing;
  campaignProperties: CampaignProperty[];
  budgetCap?: number;
  dailyCap?: number;
  pacingBudget?: number;
  pacingBudgetMode?: BudgetModes;
  pacingModifiedAt?: DateTime;
  pacingStrategy?: string;
  priority?: number;
  margins: MarginValue[];
  daypart: string = FullDaypart;
  lastPlayedTime?: DateTime;
  createdBy: number;
  campaignInternalProperties: ICampaignPropertyAPI[];
  planID?: number;
  reachAndFrequencyStatus: ReachAndFrequencyStatus;
  sharedEntity?: SharedPublicEntity;
  uuid?: string;
  plan?: {
    plan_id: number;
    uuid: string;
  };
  flights?: Flight[];
  campaignType: CampaignType;
  insertionOrderBudget: number;
  organizationType: OrganizationType.SUPPLY | OrganizationType.DEMAND;
  isTestCampaign: boolean;

  organization?: IOrganizationSharedCampaign;

  advertiserBudget?: number;

  constructor(
    id = -1,
    name = '',
    startDate?: DateTime,
    endDate?: DateTime,
    advertiserID?: number,
    advertiser?: Advertiser,
    budget = 0,
    targetedImpressions = 0,
    useManualImpressions = false,
    lineItems: LineItem[] = [],
    iabCategories: IABCategory[] = [],
    notes?: string,
    accountID?: number,
    account?: Account,
    organizationID?: number,
    minSecondsBetweenPlays = 0,
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    cachedPacing?: CachedPacing,
    campaignProperties: CampaignProperty[] = [],
    budgetCap?: number,
    dailyCap?: number,
    pacingBudget?: number,
    pacingBudgetMode?: BudgetModes,
    pacingModifiedAt?: DateTime,
    pacingStrategy?: string,
    priority?: number,
    margins: MarginValue[] = [],
    daypart: string = FullDaypart,
    lastPlayedTime?: DateTime,
    createdBy?: number,
    campaignInternalProperties: ICampaignPropertyAPI[] = [],
    planID?: number,
    reachAndFrequencyStatus: ReachAndFrequencyStatus = ReachAndFrequencyStatus.NOT_REQUESTED,
    sharedEntity?: SharedPublicEntity,
    uuid?: string,
    flights?: Flight[],
    campaignType = CampaignType.Normal,
    insertionOrderBudget?: number,
    isTestCampaign = false,
    advertiserBudget?: number,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.startDate = startDate;
    this.endDate = endDate;
    this.advertiserID = advertiserID;
    this.accountID = accountID;
    this.advertiser = advertiser;
    this.account = account;
    this.budget = budget;
    this.targetedImpressions = targetedImpressions;
    this.useManualImpressions = useManualImpressions;
    this.lineItems = lineItems;
    this.iabCategories = iabCategories;
    this.notes = notes;
    this.timestamp = DateTime.local();
    this.organizationID = organizationID;
    this.minSecondsBetweenPlays = minSecondsBetweenPlays;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.cachedPacing = cachedPacing;
    this.campaignProperties = campaignProperties;
    this.budgetCap = budgetCap;
    this.dailyCap = dailyCap;
    this.pacingBudget = pacingBudget;
    this.pacingBudgetMode = pacingBudgetMode;
    this.pacingModifiedAt = pacingModifiedAt;
    this.pacingStrategy = pacingStrategy;
    this.priority = priority;
    this.margins = margins;
    this.daypart = daypart;
    this.lastPlayedTime = lastPlayedTime;
    this.createdBy = createdBy;
    this.campaignInternalProperties = campaignInternalProperties;
    this.planID = planID;
    this.reachAndFrequencyStatus = reachAndFrequencyStatus;
    this.sharedEntity = sharedEntity;
    this.uuid = uuid;
    this.flights = flights;
    this.campaignType = campaignType;
    this.insertionOrderBudget = insertionOrderBudget;
    this.isTestCampaign = isTestCampaign;
    this.advertiserBudget = advertiserBudget;

    // Only for public campaign
    this.organizationType = null;
    this.organization = null;

    this.apiObject = null;
    this.cachedOriginalObject = null;
  }

  static initialize() {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Campaign.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Campaign.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Campaign.entityPropertiesCached = null;
    });
  }

  get status() {
    let status = 'running';
    if (this.startDate === null || this.startDate > DateTime.local()) {
      status = 'upcoming';
    } else if (this.endDate !== null && this.endDate < DateTime.local().plus({ days: -1 })) {
      status = 'completed';
    }
    return status;
  }

  get displayName() {
    return `${this.id} - ${this.name}`;
  }

  get advertiserName() {
    return this.advertiser.uniqueAdvertiser != null ? this.advertiser.uniqueAdvertiserName : this.advertiser.name;
  }

  get advertiserDescription() {
    return this.advertiser.uniqueAdvertiser != null
      ? `${this.advertiserName} (${this.advertiserDomain})`
      : this.advertiser.description;
  }

  get advertiserDomain() {
    return this.advertiser.domain != null ? this.advertiser.domain : this.advertiser.uniqueAdvertiserDomain;
  }

  get advertiserLogo() {
    return this.advertiser.uniqueAdvertiser != null ? this.advertiser.uniqueAdvertiserLogoUrl : null;
  }

  get accountName() {
    return this.account ? this.account.name : DASH;
  }

  get accountLogoUrl() {
    return this.account ? this.account.logoUrl : null;
  }

  get endpoint() {
    return `/campaigns/${this.id}`;
  }

  get startDateFormatted() {
    return this.startDate !== undefined && this.startDate !== null
      ? this.startDate.toLocaleString(DateTime.DATE_SHORT)
      : DASH;
  }

  get endDateFormatted() {
    return this.endDate !== undefined && this.startDate !== null
      ? this.endDate.toLocaleString(DateTime.DATE_SHORT)
      : DASH;
  }

  get budgetFormatted() {
    if (this.pacingBudget != null && this.pacingBudgetMode === 'B') {
      return this.pacingBudgetFormatted;
    }
    return this.budget === null ? DASH : i18n.global.n(this.budget, 'currency');
  }

  get targetedImpressionsFormatted() {
    if (this.pacingBudget != null && this.pacingBudgetMode === 'I') {
      return this.pacingBudgetFormatted;
    }
    return DASH;
  }

  get totalImpressionsFormatted() {
    return this.cachedPacing != null && this.cachedPacing?.totalImpressionsFormatted !== '0'
      ? this.cachedPacing.totalImpressionsFormatted
      : DASH;
  }

  get totalSpendFormatted() {
    if (this.isSupplyOrganization) {
      return this.totalSpendOwnerFormatted;
    }
    return this.totalSpendBuyerFormatted;
  }

  get averageCPMFormatted() {
    if (this.isSupplyOrganization) {
      return this.averageCPMOwnerFormatted;
    }
    return this.averageCPMBuyerFormatted;
  }

  get averageCPMBuyerFormatted() {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMBuyerFormatted : DASH;
  }

  get averageCPMOwnerFormatted() {
    return this.cachedPacing != null ? this.cachedPacing.averageCPMOwnerFormatted : DASH;
  }

  get totalSpendOwnerFormatted() {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendOwnerFormatted : DASH;
  }

  get totalSpendBuyerFormatted() {
    return this.cachedPacing != null ? this.cachedPacing.totalSpendBuyerFormatted : DASH;
  }

  get minSecondsBetweenPlaysFormatted() {
    return `${formatNumber(this.minSecondsBetweenPlays, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get createdOnFormatted() {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get cachedReportLastUpdatedOnFormatted() {
    return this.cachedPacing?.cachedReportLastUpdatedOnFormatted || DASH;
  }

  get budgetCapFormatted() {
    if (this.budgetCap == null) {
      return null;
    }
    return i18n.global.n(this.budgetCap, 'currency');
  }

  get remainingBudget() {
    if (this.budgetCap == null) {
      return null;
    }
    if (!this.isCampaignBudgetCapEnabled) {
      return 0;
    }
    const budget = this.budget || 0;
    return Math.max(0, this.budgetCap - budget);
  }

  get usedBudget() {
    if (this.budgetCap == null) {
      return null;
    }
    return this.budget || 0;
  }

  get reversedPriority() {
    const priority = 10 - this.priority;
    return priority === 10 ? null : priority;
  }

  get pacingBudgetFormatted() {
    return this.pacingBudgetMode === 'B'
      ? i18n.global.n(this.pacingBudget, 'currency')
      : i18n.global.n(this.pacingBudget);
  }

  get insertionOrderBudgetFormatted() {
    return this.insertionOrderBudget ? i18n.global.n(this.insertionOrderBudget, 'currency') : DASH;
  }

  get dailyCapFormatted() {
    if (this.dailyCap == null) return DASH;
    return this.pacingBudgetMode === 'B' ? i18n.global.n(this.dailyCap, 'currency') : i18n.global.n(this.dailyCap);
  }

  get isCampaignBudgetCapEnabled() {
    const orgSettings = getStore().state?.orgApp?.organizationSettings;
    return orgSettings !== null && orgSettings.enableCampaignBudgetCapField;
  }

  get isCampaignPacingEnabled() {
    return this.pacingBudget != null;
  }

  get isCampaignFlightsEnabled() {
    return this.isCampaignPacingEnabled && this.flights?.length > 0;
  }

  get flightEnableDisabledMessage(): string {
    return i18n.global.t('messages.flightActionDisabled');
  }

  get createdFromPlanURL(): string {
    return this.planID != null ? `/plans/${this.planID}` : null;
  }

  get publicCampaignURL(): string {
    if (this.sharedEntity?.active) {
      return `/monitoring/${this.uuid}`;
    }
    return null;
  }

  toggleCampaignPacing() {
    if (this.pacingBudget != null) {
      this.pacingBudget = null;
      this.pacingBudgetMode = null;
      this.pacingStrategy = null;
      this.pacingModifiedAt = null;
      this.dailyCap = null;
      this.timestamp = DateTime.local();
    } else {
      this.pacingBudget = 0;
      this.pacingBudgetMode = BudgetModes.BUDGET;
      this.pacingStrategy = 'audience';
      this.pacingModifiedAt = DateTime.local();
    }
  }

  get hasStarted() {
    return DateTime.local().startOf('day') >= this.startDate?.startOf('day');
  }

  get hasEnded() {
    return DateTime.local().startOf('day') > this.endDate?.startOf('day');
  }

  get durationInHours() {
    return this.endDate.plus({ days: 1 }).diff(this.startDate, 'hours').toObject().hours;
  }

  get durationInDays() {
    return this.endDate.plus({ days: 1 }).diff(this.startDate, 'days').toObject().days;
  }

  get dailyCapWarning() {
    let warning = null;
    if (this.dailyCap != null && this.durationInDays > 0 && !this.hasEnded) {
      if (this.pacingBudgetMode === BudgetModes.BUDGET) {
        const min = Math.ceil(this.budget / this.durationInDays);
        if (this.dailyCap < min) {
          const underspending = this.budget - this.durationInDays * this.dailyCap;
          warning = `${i18n.global.t('messages.dailyGoalCapWarningForCampaigns', [i18n.global.n(underspending, 'currency'), i18n.global.n(min, 'currency')])}`;
        }
      } else {
        const min = Math.ceil(this.targetedImpressions / this.durationInDays);
        if (this.dailyCap < min) {
          const underspending = this.targetedImpressions - this.durationInDays * this.dailyCap;
          warning = `${i18n.global.t('messages.dailyGoalCapWarningForCampaigns', [i18n.global.n(underspending), i18n.global.n(min)])}`;
        }
      }
    }
    return warning;
  }

  get ellapsedInHours() {
    if (this.hasEnded) {
      return this.durationInHours;
    }
    if (!this.hasStarted) {
      return 0;
    }
    const zone = getStore().state?.orgApp?.organization?.timezone;
    return DateTime.fromISO(DateTime.local().toJSDate().toISOString(), { zone })
      .diff(DateTime.fromISO(this.startDate.toFormat('yyyy-MM-dd'), { zone }), 'hours')
      .toObject().hours;
  }

  /**
   * This is used for flighted Line Items
   * Those LI cachedPacing only contains the data for the running flight
   * The completed LI don't have any data and will show 0 as progress, even though there was some ad/impressions spent
   * This method will calculate the progress based on the (total spend/impressions) / goal
   */
  get uiProgress(): number {
    const denominator = this.pacingBudgetMode === BudgetModes.BUDGET ? this.pacingBudget : this.targetedImpressions;
    const budgetNumerator = LineItem.isSupplyOrganization
      ? this.cachedPacing?.totalSpendOwner
      : this.cachedPacing?.totalSpendBuyer;
    const numerator =
      this.pacingBudgetMode === BudgetModes.BUDGET ? budgetNumerator : this.cachedPacing?.totalImpressions;

    return denominator === 0 ? 0 : numerator / denominator;
  }

  get pacing(): number {
    if (this.cachedPacing != null) {
      if (this.cachedPacing.totalImpressions === 0) {
        return null;
      }
      return this.cachedPacing.pacing;
    }

    return null;
  }

  get pacingFormatted(): string {
    if (this.pacing === null || this.pacing === 0) {
      return DASH;
    }

    return formatNumber(this.pacing, '0.00%');
  }

  get pacingVsEven() {
    if (this.isCampaignFlightsEnabled && this.status === 'completed') {
      return this.uiProgress;
    }

    if (this.cachedPacing != null && this.durationInHours !== 0) {
      if (this.pacingBudgetMode === 'B') {
        if (this.pacingBudget !== 0) {
          return (
            this.cachedPacing.totalSpendBuyer /
            ((this.pacingBudget / this.durationInHours) * Math.floor(this.ellapsedInHours))
          );
        }
      } else if (this.pacingBudgetMode === 'I') {
        if (this.pacingBudget !== 0) {
          return (
            this.cachedPacing.totalImpressions /
            ((this.pacingBudget / this.durationInHours) * Math.floor(this.ellapsedInHours))
          );
        }
      }
    }
    return null;
  }

  get pacingVsEvenFormatted() {
    if (this.pacingVsEven == null || Number.isNaN(this.pacingVsEven)) {
      return DASH;
    }
    return formatNumber(this.pacingVsEven, '0.00%');
  }

  get progressFormatted() {
    let progress = 0;

    if (this.isCampaignFlightsEnabled && this.status === 'completed') {
      progress = this.uiProgress;
    } else if (this.cachedPacing != null) {
      progress = this.cachedPacing.progress;
    }

    if (progress !== 0) {
      return formatNumber(progress, '0.00%');
    }
    return DASH;
  }

  get pacingColorIndicatorClassName() {
    if (this.pacing == null || this.pacing === 0) {
      return '';
    }
    if (this.pacing < 0.8) {
      return 'badge-danger';
    }
    if (this.pacing < 0.95) {
      return 'badge-warning';
    }
    return 'badge-success';
  }

  get pacingVsEvenColorIndicatorClassName() {
    if (this.cachedPacing == null || this.pacingVsEven == null || this.pacingVsEven === 0) {
      return '';
    }
    if (this.pacingVsEven < 0.8) {
      return 'badge-danger';
    }
    if (this.pacingVsEven < 0.95) {
      return 'badge-warning';
    }
    return 'badge-success';
  }

  get hasDaypartEnabledInTheLast24Hours() {
    return this.hasDaypartEnabledInTheLastXHours(24);
  }

  get hasDaypartEnabledInTheLast12Hours() {
    return this.hasDaypartEnabledInTheLastXHours(12);
  }

  get didNotPlayInTheLast24Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast24Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 24;
  }

  get didNotPlayInTheLast12Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 12;
  }

  get didPlayInTheLast12Hours() {
    if (this.hasEnded) {
      return false;
    }
    if (this.lastPlayedTime == null) {
      return false; // Did not start playing.
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return true; // Not really playing but still should show a green thumb in the UI.
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours <= 12;
  }

  hasDaypartEnabledInTheLastXHours(hours = 24) {
    if (!this.daypart) return false;

    const startOfWeek = DateTime.local().startOf('week');
    const hoursSinceStartOfWeek = DateTime.local().startOf('hour').diff(startOfWeek, 'hours').toObject().hours;
    const ranges = [
      {
        start: Math.max(0, hoursSinceStartOfWeek - hours),
        end: hoursSinceStartOfWeek,
      },
    ];
    if (hoursSinceStartOfWeek < hours) {
      ranges.push({
        start: this.daypart.length + hoursSinceStartOfWeek - hours,
        end: this.daypart.length,
      });
    }
    return ranges.reduce((mem, x) => mem || this.daypart.slice(x.start, x.end - x.start).includes('1'), false);
  }

  get lastPlayedTimeFormatted() {
    return this.lastPlayedTime != null ? this.lastPlayedTime.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get isSupplyOrganization() {
    return mapOrgAppGetters().isSupplyOrganization.value;
  }

  get originalObject(): Campaign {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Campaign.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'account',
      'advertiser',
      'archived',
      'cachedpacing',
      'campaigncachedpacing_id',
      'created_by',
      'created_on_utc',
      'daypart',
      'end_date',
      'exchange_id',
      'flights',
      'modified_by',
      'modified_on_utc',
      'organization_id',
      'plan',
      'sharedcampaign_id',
      'start_date',
      'uuid',
      'last_played_on_utc',
      'campaign_properties',
      'shared_campaign',
      'shared_entity',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    const prepareCampaignPropertiesForDiff = (properties: ICampaignPropertyAPI[]) =>
      properties
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((x) => ({ key: x.key, code: x.code, name: x.name, value: x.value }));

    const originalCampaignProperties = prepareCampaignPropertiesForDiff(this.apiObject.campaign_properties);
    const updatedCampaignProperties = prepareCampaignPropertiesForDiff(this.toApi().campaign_properties);
    const campaignPropertiesDiff = getDiff(originalCampaignProperties, updatedCampaignProperties);

    const originalCampaignInternalProperties = prepareCampaignPropertiesForDiff(
      this.apiObject.campaign_internal_properties,
    );
    const updatedCampaignInternalProperties = prepareCampaignPropertiesForDiff(
      this.toApi().campaign_internal_properties,
    );
    const campaignInternalPropertiesDiff = getDiff(
      originalCampaignInternalProperties,
      updatedCampaignInternalProperties,
    );

    const flightsDiff = this.flights?.filter((flight) => !flight.isPristine());
    if (
      diffs.length === 0 &&
      (flightsDiff == null || flightsDiff.length === 0) &&
      campaignPropertiesDiff.length === 0 &&
      campaignInternalPropertiesDiff.length === 0
    )
      return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  async getChanges(): Promise<IEntityChange<Campaign>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Campaign>(this.originalObject, Campaign.entityProperties);
  }

  static fromObject(obj) {
    return super.fromObject(obj, new Campaign());
  }

  static fromApi(apiObject: ICampaignAPI): Campaign {
    const campaign = new Campaign(
      apiObject.campaign_id,
      apiObject.name,
      apiObject.start_date !== undefined && apiObject.start_date !== null
        ? DateTime.fromISO(apiObject.start_date)
        : null,
      apiObject.end_date !== undefined && apiObject.end_date !== null ? DateTime.fromISO(apiObject.end_date) : null,
      apiObject.advertiser_id,
      apiObject.advertiser != null ? Advertiser.fromApi(apiObject.advertiser) : null,
      apiObject.budget,
      apiObject.targeted_impressions,
      apiObject.use_manual_impressions,
      [],
      apiObject.iab_categories,
      apiObject.notes,
      apiObject.account_id,
      apiObject.account ? Account.fromApi(apiObject.account) : null,
      apiObject.organization_id,
      apiObject.min_seconds_between_plays,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.cachedpacing != null ? CachedPacing.fromApi(apiObject.cachedpacing) : null,
      apiObject.campaign_properties != null
        ? apiObject.campaign_properties.map((x) => CampaignProperty.fromApi(x))
        : [],
      apiObject.budget_cap,
      apiObject.cap,
      apiObject.pacing_budget,
      apiObject.pacing_budget_mode,
      fromServerDateTime(apiObject.pacing_modified_at),
      apiObject.pacing_strategy,
      apiObject.priority,
      apiObject.margins ? apiObject.margins.map((x) => MarginValue.fromApi(x)) : [],
      apiObject.daypart,
      apiObject.last_played_on_utc != null ? DateTime.fromISO(apiObject.last_played_on_utc, { zone: 'utc' }) : null,
      apiObject.created_by,
      apiObject.campaign_internal_properties,
      apiObject.plan_id,
      apiObject.reach_and_frequency_status,
      apiObject.shared_entity != null ? SharedPublicEntity.fromSharedPublicEntityApi(apiObject.shared_entity) : null,
      apiObject.uuid,
      apiObject.flights ? apiObject.flights.map((flight) => Flight.fromApi(flight)) : null,
      apiObject.campaign_type ?? CampaignType.Normal,
      apiObject.insertion_order_budget,
      apiObject.test_campaign ?? false,
      apiObject.advertiser_budget,
    );

    if (apiObject.flights) {
      campaign.flights = apiObject.flights.map((flight) => Flight.fromApi(flight, campaign));
    }

    campaign.apiObject = apiObject;

    return campaign;
  }

  toApi(): ICampaignAPI {
    return {
      campaign_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      advertiser_id: this.advertiserID,
      account_id: this.accountID,
      budget: this.budget,
      notes: this.notes,
      targeted_impressions: this.targetedImpressions,
      use_manual_impressions: this.useManualImpressions,
      min_seconds_between_plays: this.minSecondsBetweenPlays,
      campaign_properties: this.campaignProperties.map((x) => x.toApi()),
      budget_cap: !this.isCampaignBudgetCapEnabled ? null : this.budgetCap,
      cap: this.dailyCap,
      pacing_budget: this.pacingBudget,
      pacing_budget_mode: this.pacingBudgetMode,
      pacing_modified_at: toServerDateTimeNoTimezone(this.pacingModifiedAt, true),
      pacing_strategy: this.pacingStrategy,
      priority: this.priority,
      margins: this.margins ? this.margins.map((x) => x.toApi()) : [],
      campaign_internal_properties: this.campaignInternalProperties,
      plan_id: this.planID,
      reach_and_frequency_status: this.reachAndFrequencyStatus,
      shared_campaign: this.sharedEntity?.toApi() ?? null,
      flights: this.flights?.map((flight) => flight.toApi()),
      campaign_type: this.campaignType ?? CampaignType.Normal,
      insertion_order_budget: this.insertionOrderBudget,
      test_campaign: this.isTestCampaign ?? false,
      advertiser_budget: this.advertiserBudget,
    };
  }

  getExtraValidation() {
    const extras: {
      [key: string]: {
        message: string | undefined;
        preEvaluated: boolean | undefined;
        value: number | undefined;
        rules: string | undefined;
      };
    } = {};
    //
    //
    // Budget cap
    //
    if (this.isCampaignBudgetCapEnabled && this.budgetCap != null) {
      // Check all line items have the same budget mode except for LI archived
      if (this.lineItems?.some((x) => x.budgetMode !== BudgetModes.BUDGET && x.state !== 'C')) {
        extras.budgetCap = {
          message: i18n.global.t('messages.aCampaignBudgetCapCanOnlyBeSetIfAllLineItemsHaveABudgetGoal'),
          preEvaluated: true,
          value: undefined,
          rules: undefined,
        };
      } else if (this.budget > this.budgetCap) {
        extras.budgetCap = {
          message: i18n.global.t('messages.theBudgetCapCannotBeLowerThanTheSumOfAllLineItemBudgets'),
          preEvaluated: true,
          value: undefined,
          rules: undefined,
        };
      }
    }

    if (this.isCampaignPacingEnabled) {
      extras.pacingBudget = {
        value: this.pacingBudget,
        rules: 'greater_than:0',
        message: undefined,
        preEvaluated: undefined,
      };

      if (this.advertiserBudget && !Number.isNaN(this.advertiserBudget)) {
        extras.advertiserBudget = {
          value: this.advertiserBudget,
          rules: 'numeric:float|max_value:2000000000',
          message: capitalize(i18n.global.t('messages.invalidAdvertiserBudget')),
          preEvaluated: undefined,
        };
      }

      if (this.isCampaignFlightsEnabled && this.flights?.some((x) => x.goal === 0)) {
        extras.flights = {
          message: i18n.global.t('messages.flightsGoalError'),
          preEvaluated: true,
          value: undefined,
          rules: undefined,
        };
      }
    }

    return extras;
  }

  clone() {
    return new Campaign(
      null,
      this.name,
      this.startDate,
      this.endDate,
      this.advertiserID,
      this.advertiser,
      this.budget,
      this.targetedImpressions,
      this.useManualImpressions,
      null,
      this.iabCategories,
      this.notes,
      this.accountID,
      this.account,
      this.organizationID,
      this.minSecondsBetweenPlays,
      null,
      null,
      null,
      this.campaignProperties,
      this.budgetCap,
      this.dailyCap,
      this.pacingBudget,
      this.pacingBudgetMode,
      this.pacingModifiedAt,
      this.pacingStrategy,
      this.priority,
      this.margins,
      this.daypart,
      null,
      null,
      this.campaignInternalProperties,
      this.planID,
      this.reachAndFrequencyStatus,
      null,
      null,
      this.flights?.map((f) => f.duplicate(f.startDate, f.endDate)) || [],
      this.campaignType,
    );
  }

  static get expands() {
    return [
      'advertiser.unique_advertiser.replacement_unique_advertiser.advertiser_domains',
      'campaign_properties',
      'shared_entity',
    ];
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:191|not_empty_or_whitespace',
      budgetCap: 'numeric:int|min_value:0.01',
      advertiserID: 'required',
      minSecondsBetweenPlays: 'required',
    };
  }

  static get entityProperties() {
    if (Campaign.entityPropertiesCached != null) {
      return Campaign.entityPropertiesCached;
    }

    Campaign.entityPropertiesCached = getEntityProperties();

    return Campaign.entityPropertiesCached;
  }

  static get entityName(): string {
    return Campaign.type;
  }

  static async getEntityIDsFromModels(models) {
    if (models.length === 0) return [];

    try {
      const getCampaignsAction = ({ queryParams, cancelTokenSource, showLoader }) =>
        getStore().dispatch('orgApp/getCampaigns', { queryParams, cancelTokenSource, showLoader });
      const qp = QueryParams.all(
        FilterCollection.for(
          'name',
          models.filter((x) => x.name != null).map((x) => x.name),
          FilterComparisonOperator.IN,
        ),
      );
      qp.showLoader = false;
      qp.include = ['campaign_id', 'name'];
      const response = await qp.run(getCampaignsAction);
      const entities = response.items;
      return models.map((model) => ({ model, id: entities.find((x) => x.name === model.name)?.id }));
    } catch (e) {
      throw new Exception('Failed to get campaign IDs.');
    }
  }

  get isRnFNotRequested() {
    return this.reachAndFrequencyStatus === ReachAndFrequencyStatus.NOT_REQUESTED;
  }

  get isRnFRequested() {
    return this.reachAndFrequencyStatus === ReachAndFrequencyStatus.REQUESTED;
  }

  get isRnFInProgress() {
    return this.reachAndFrequencyStatus === ReachAndFrequencyStatus.IN_PROGRESS;
  }

  get isRnFCompleted() {
    return this.reachAndFrequencyStatus === ReachAndFrequencyStatus.COMPLETED;
  }

  get flightExpand(): string {
    return CampaignExpandOptions.Flights;
  }

  // not that we allow to go over budget, so having a ratio +100 is a valid use case
  get usedBudgetRatio(): number {
    const ratios = [];
    this.lineItems.forEach((x) => {
      if (x.flightRatio) {
        ratios.push(x.flightRatio);
      }
    });
    return ratios.length === 0 ? 0 : QuickMathUtils.sum(ratios);
  }

  // campaign_type was introduced by the backend recently and unfortunately, some types for smart campaign are wrong, flagging them as NORMAL instead of SMART
  // this is a temporary fix to make sure that we can still use the campaign_type field in the frontend.
  get uiCampaignType(): CampaignType {
    if (this.campaignType === CampaignType.Normal && this.isCampaignPacingEnabled && !this.isCampaignFlightsEnabled) {
      return CampaignType.Smart;
    }

    if (this.campaignType === CampaignType.Normal && this.isCampaignPacingEnabled && this.isCampaignFlightsEnabled) {
      return CampaignType.Flight;
    }

    return this.campaignType;
  }

  get campaignTypeFormatted(): string {
    return capitalize(this.uiCampaignType.replace('_', ' ').toLowerCase());
  }
}

Campaign.initialize();
